import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import SelectInput from '../../pro/components/orderform/SelectInput';
import TextInput from '../../pro/components/orderform/TextInput';
import FieldValue from './FieldValue';
import DateTimePicker from '../../../components/DateTimePicker';
import RevisionHistoryModal from './RevisionHistoryModal';
import AlertModal from '../../../components/ui/AlertModal';
import { FontScaleControl, useCardFontScale } from './QuoteCardScaleContext';
import type { QuoteDetailData } from '../fixtures';
import { REV_REASONS, SOLD_BY, withCurrent } from '../quoteOptions';
import { isFutureEffective, isPastEndDate } from '../quoteFieldRules';
import './quoteDetailHeader.css';

/** "MM/DD/YYYY" → Date (null when blank / unparseable) for the date picker. */
function parseUsDate(s?: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec((s ?? '').trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date → "MM/DD/YYYY" (the display format the save layer's toISO() expects). */
function fmtUsDate(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

/** Per-field "this date is unusually far out" warning thresholds (days from
 *  today). Editing a date beyond its limit pops an acknowledge-only warning —
 *  the value is still accepted. */
const DATE_WARN_DAYS = { effective: 30, reviewDate: 120, endDate: 360 } as const;
const DATE_WARN_LABEL = { effective: 'Effective date', reviewDate: 'Review Date', endDate: 'End Date' } as const;
type WarnableDateField = keyof typeof DATE_WARN_DAYS;

/** Whole calendar days from today (midnight) to `d`; negative if in the past. */
function daysFromToday(d: Date): number {
  const today = new Date();
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

// Intelligence (actualized) stat formatters — Revision Statistics column.
const fmtIntelVol = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtIntelRev = (v: number) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtIntelMargin = (v: number | null) => (v == null ? '—' : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

interface Props {
  data: QuoteDetailData;
  /**
   * Optional change callback for the editable revision/lifecycle fields. When
   * provided, ONLY the fields the save layer (REVISION_MAP) actually persists
   * are editable; every other input stays read-only so "editable" and
   * "persisted" match (no silent-drop edit trap). Editable fields emit
   * `(field, value)` where `field` is the camelCase `QuoteDetailData` key
   * (matching the `value=` source) and `value` is the new string. When ABSENT,
   * every field renders read-only display exactly as before — existing
   * read-only callers are unaffected. The camelCase keys map to backend
   * snake_case columns (effective→effectivedate, endDate→enddate via REVISION_MAP;
   * reviewDate→masterquote.reviewdate via HEADER_MAP; etc.).
   *
   * Editable when the callback is provided (and ONLY these): rawStatus,
   * reviewDate, effective, endDate, revReason, plus the Revenue column's
   * volume / estRev / estMargin (quoterevisions.volume / estimatedrevenue /
   * estimatedmargin via REVISION_MAP).
   *
   * NOTE: the Status control edits `rawStatus` (the raw quoterevisions.status —
   * UNSECURED/SECURED, V1's only two values), NOT the derived `status`
   * (ACTIVE/EXPIRING/DRAFT/… computed for display). Securing is the V1 notion of
   * "approving"; there is no separate APPROVED state.
   *
   * Read-only regardless of this callback: quotedBy and salesRep (Ownership —
   * not in the save payload map, so visually present but not persisted);
   * statusDate / Secured Date (display-only — auto-stamped when the quote is
   * SECURED, never hand-edited); lastUsed, daysSince (system-derived); and
   * createDate (already hard read-only).
   */
  onRevisionChange?: (field: keyof QuoteDetailData, value: string) => void;
  /** Show the built-in Revision History link in the card header (default true).
   *  The editor drawer passes false — it surfaces the link in the DRAWER header
   *  and owns its own modal instead. */
  showRevisionHistory?: boolean;
  /** When the quote is SECURED it is locked: every revision field renders
   *  read-only EXCEPT End Date (V1 keepopenfields="|enddate|"). A "Locked" badge
   *  is shown. `onRevisionChange` is still provided so End Date stays editable. */
  secured?: boolean;
  /** Controlled collapse state for the Quote Details section. When provided, the
   *  header is CONTROLLED (renders this value, reports toggles via
   *  onCollapsedChange) so the drawer / page can persist collapse to user
   *  settings. Omit for the default uncontrolled behavior (starts expanded). */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Opt into per-card font scaling (same as Card's `scaleKey`). When set AND
   *  rendered inside a QuoteCardScaleProvider (the editor drawer), the header
   *  shows a hover `− % +` control that zooms the section body. The standalone
   *  Quote Detail page passes nothing → no control. */
  scaleKey?: string;
}

/** Fields that become editable inputs when `onRevisionChange` is provided.
 *  Everything else renders as a read-only <FieldValue>. */
const REVISION_EDITABLE: ReadonlySet<keyof QuoteDetailData> = new Set([
  'reviewDate', 'effective', 'endDate', 'revReason', 'soldBy',
  'volume', 'estRev', 'estMargin',
]);

/**
 * Quote Details header card on the Quote Detail page. Three columns: Dates,
 * Status, Ownership. Top-right: the Revision History link (standalone page
 * only — the drawer hoists its own). Prev/Next, the quote #, the type pill and
 * the SECURED "Locked" badge live in the drawer header.
 */
const QuoteDetailHeader: React.FC<Props> = ({ data, onRevisionChange, showRevisionHistory = true, secured = false, collapsed: collapsedProp, onCollapsedChange, scaleKey }) => {
  const navigate = useNavigate();
  // Per-section font scaling (drawer only — the standalone page passes no
  // scaleKey and mounts no provider). Zooms the qdh body; control lives in the
  // header. Mirrors Card's use of the same hook.
  const { fontScalable, scale, bodyStyle } = useCardFontScale(scaleKey);
  const [showHistory, setShowHistory] = useState(false);
  // Collapse is uncontrolled by default; when `collapsed` is provided the header
  // is controlled and reports toggles via onCollapsedChange (persisted upstream).
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsedControlled = collapsedProp !== undefined;
  const collapsed = isCollapsedControlled ? collapsedProp : internalCollapsed;
  const toggleCollapsed = () => {
    const next = !collapsed;
    if (!isCollapsedControlled) setInternalCollapsed(next);
    onCollapsedChange?.(next);
  };
  // Revision History needs a persisted quote (a quotenumber); it's unavailable
  // while creating a quote, so the link is disabled until the quote exists.
  const hasQuote = data.quoteNumber != null;
  // Acknowledge-only warning when an edited date lands further out than its
  // usual window (Effective > 30d, Review Date > 120d, End Date > 360d).
  const [dateWarning, setDateWarning] = useState<string | null>(null);
  const warnIfFar = (field: WarnableDateField, d: Date | null) => {
    if (!d) return;
    const days = daysFromToday(d);
    const limit = DATE_WARN_DAYS[field];
    if (days > limit) {
      setDateWarning(`The ${DATE_WARN_LABEL[field]} is ${days.toLocaleString()} days out — beyond the usual ${limit}-day window. Double-check that's correct.`);
    }
  };

  // A field is editable iff the page provided the callback (editable quote) AND
  // the field is in REVISION_EDITABLE. Otherwise it renders as a <FieldValue>.
  // N12: the revision reason is locked on revision 1 (V1 fv_businesstype).
  // A SECURED quote locks every field except End Date (V1 keepopenfields).
  const isEditable = (field: keyof QuoteDetailData) =>
    Boolean(onRevisionChange) && REVISION_EDITABLE.has(field)
    && !(field === 'revReason' && data.revisionNumber === 1)
    && !(secured && field !== 'endDate');

  // onChange handler for a field — only defined when editing is enabled.
  const ch = (field: keyof QuoteDetailData) =>
    onRevisionChange ? (v: string) => onRevisionChange(field, v) : undefined;

  return (
    <div className="bg-panel px-[18px] pt-2 pb-2.5">
      <section className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden ${fontScalable ? 'group/card-scale' : ''}`}>
        {/* Pro-drawer header style: clean header (no gray strip), muted uppercase
            title + blue icon on the left, chevron on the right, dashed underline. */}
        <div className={`px-3 pt-2 ${collapsed ? 'pb-2' : ''}`}>
        <div className={`flex items-center justify-between gap-2 ${collapsed ? '' : 'pb-2 mb-1 border-b border-dashed border-slate-200 dark:border-slate-700'}`}>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} Quote Details`}
            className="flex items-center gap-1.5 min-w-0 flex-1 cursor-pointer bg-transparent border-0 p-0 text-left"
          >
            <h3 className="m-0 text-[0.65625rem] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider inline-flex items-center gap-1.5">
              <DocumentIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              Quote Details
            </h3>
          </button>
          {/* Prev/Next, the quote #, the quote-type pill and the SECURED
              "Locked" badge all live in the DRAWER header now, not this card.
              Only the Revision History link remains (standalone page; the drawer
              hoists its own and passes showRevisionHistory={false}). */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {showRevisionHistory && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                disabled={!hasQuote}
                className="text-[0.75rem] font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:text-slate-300 dark:disabled:text-slate-600 disabled:no-underline disabled:cursor-default"
              >
                Revision History
              </button>
            )}
            <FontScaleControl scaleKey={scaleKey} title="Quote Details" />
            <span
              aria-hidden="true"
              onClick={toggleCollapsed}
              className="inline-flex cursor-pointer"
            >
              <ChevronDownIcon strokeWidth={2.5} className={`h-3 w-3 flex-shrink-0 transition-transform text-slate-400 dark:text-slate-500 ${collapsed ? '-rotate-90' : ''}`} />
            </span>
          </div>
        </div>
        </div>
        {!collapsed && (
        <div className="px-3 py-2 qdh-wrap" style={bodyStyle} data-font-scale={fontScalable ? scale : undefined}>
          <div className="qdh-cols">
            <MetaCol title="Ownership">
              {/* Quoted By = the creator (quoterevisions.quotedby), read-only. */}
              <Row><Label width={104}>Quoted By</Label><FieldValue value={data.quotedBy} /></Row>
              {/* Sold By = editable enum (Business Development / Inside Sales / blank). */}
              <Row>
                <Label width={104}>Sold By</Label>
                {isEditable('soldBy')
                  ? <SelectInput value={data.soldBy ?? ''} onChange={ch('soldBy')} options={withCurrent(data.soldBy ?? '', ['', ...SOLD_BY])} placeholder="—" />
                  : <FieldValue value={data.soldBy} />}
              </Row>
              <Row><Label width={104}>Sales Rep</Label><FieldValue value={data.salesRep} /></Row>
              <Row><Label width={104}>Create Date</Label><FieldValue value={data.createDate} /></Row>
              <Row className="flex-wrap">
                <Label width={104}>Status / Date</Label>
                {/* Display-only (not edited here): the raw stored SECURED /
                    UNSECURED, then the auto-stamped secure date sitting right
                    beside it (fixed status width keeps the date adjacent rather
                    than flexing to the far edge of the row). Both values are
                    fixed-width, so they're grouped and the row wraps: in a
                    narrow column the pair drops to a second line instead of
                    spilling over the neighbouring column's labels. */}
                <span className="flex items-center gap-2 min-w-0">
                  <FieldValue value={data.rawStatus} width={92} />
                  <FieldValue value={data.statusDate} width={92} />
                </span>
              </Row>
            </MetaCol>
            <MetaCol title="Dates">
              {/* Rev. Reason only applies to a revision (rev > 1); the original
                  quote (rev 1) has none, so it's hidden there. */}
              {(data.revisionNumber ?? 0) > 1 && (
                <Row>
                  <Label width={104}>Rev. Reason</Label>
                  {isEditable('revReason') ? <SelectInput options={Array.from(new Set(['—', ...withCurrent(data.revReason, REV_REASONS)]))} value={data.revReason} onChange={ch('revReason')} /> : <FieldValue value={data.revReason} />}
                </Row>
              )}
              {/* A future effective date highlights the WHOLE row in purple
                  (V1 cue), matching the Key Account row style. The negative
                  margin lets the fill bleed into the card padding so the label
                  stays aligned with the rows above/below. */}
              <Row className={isFutureEffective(data.effective) ? 'rounded -mx-2 px-2 bg-purple-50 border border-purple-400 dark:bg-purple-900/30 dark:border-purple-600' : undefined}>
                <Label width={104}>Effective</Label>
                {isEditable('effective') ? (
                  <div className="flex-1 min-w-0 max-w-[125px]">
                    <DateTimePicker
                      mode="date"
                      selectionType="single"
                      value={parseUsDate(data.effective)}
                      onChange={(v) => { const d = v instanceof Date ? v : null; onRevisionChange?.('effective', d ? fmtUsDate(d) : ''); warnIfFar('effective', d); }}
                      labelType="none"
                      ariaLabel="Effective"
                      placeholder="—"
                      compact
                      autoApply
                      usePortal
                      autoFlip
                      className="w-full"
                      // Inside the purple row fill, the input keeps its own box with
                      // a purple border + tint (mirrors the Total w/Charges input).
                      inputClassName={isFutureEffective(data.effective) ? '!bg-purple-50 !border-purple-400 dark:!bg-purple-900/30 dark:!border-purple-600' : ''}
                    />
                  </div>
                ) : (
                  <FieldValue value={data.effective} className={isFutureEffective(data.effective) ? 'text-purple-800 font-semibold dark:text-purple-300' : undefined} />
                )}
              </Row>
              <Row>
                <Label width={104}>Review Date{isEditable('reviewDate') && <span className="text-red-500"> *</span>}</Label>
                {isEditable('reviewDate') ? (
                  <div className={`flex-1 min-w-0 max-w-[125px] ${!(data.reviewDate ?? '').trim() ? 'rounded ring-1 ring-red-400 dark:ring-red-500' : ''}`}>
                    <DateTimePicker
                      mode="date"
                      selectionType="single"
                      value={parseUsDate(data.reviewDate)}
                      onChange={(v) => { const d = v instanceof Date ? v : null; onRevisionChange?.('reviewDate', d ? fmtUsDate(d) : ''); warnIfFar('reviewDate', d); }}
                      labelType="none"
                      ariaLabel="Review Date"
                      placeholder="—"
                      compact
                      autoApply
                      usePortal
                      autoFlip
                      className="w-full"
                    />
                  </div>
                ) : (
                  <FieldValue value={data.reviewDate} />
                )}
              </Row>
              {/* A past end date (quote already ended) highlights the whole row
                  in grey, mirroring the purple future-effective cue. */}
              <Row className={isPastEndDate(data.endDate) ? 'rounded -mx-2 px-2 bg-slate-100 border border-slate-400 dark:bg-slate-700/40 dark:border-slate-500' : undefined}>
                <Label width={104}>End Date</Label>
                {isEditable('endDate') ? (
                  <div className="flex-1 min-w-0 max-w-[125px]">
                    <DateTimePicker
                      mode="date"
                      selectionType="single"
                      value={parseUsDate(data.endDate)}
                      onChange={(v) => { const d = v instanceof Date ? v : null; onRevisionChange?.('endDate', d ? fmtUsDate(d) : ''); warnIfFar('endDate', d); }}
                      labelType="none"
                      ariaLabel="End Date"
                      placeholder="—"
                      compact
                      autoApply
                      usePortal
                      autoFlip
                      className="w-full"
                      // Inside the grey row fill, the input keeps its own box with a
                      // grey border + tint (mirrors the Effective highlight).
                      inputClassName={isPastEndDate(data.endDate) ? '!bg-slate-100 !border-slate-400 dark:!bg-slate-700/40 dark:!border-slate-500' : ''}
                    />
                  </div>
                ) : (
                  <FieldValue value={data.endDate} className={isPastEndDate(data.endDate) ? 'text-slate-700 font-semibold dark:text-slate-300' : undefined} />
                )}
              </Row>
              <Row className="flex-wrap">
                <Label width={104}>Last Used / Days</Label>
                {/* Same fixed-width pair / wrap treatment as Status / Date. */}
                <span className="flex items-center gap-2 min-w-0">
                  <FieldValue value={data.lastUsed} width={92} />
                  <FieldValue value={data.daysSince} width={56} />
                </span>
              </Row>
            </MetaCol>
            <MetaCol title="Revenue">
              {/* Per-quote economics (quoterevisions) — what the sales rep entered.
                  Est. Revenue auto-calcs upstream as (dray + FSC adj) × volume. */}
              <Row>
                <Label width={104}>Volume (Yearly)</Label>
                {isEditable('volume') ? <TextInput value={data.volume} onChange={ch('volume')} ariaLabel="Volume (Yearly)" placeholder="—" /> : <FieldValue value={data.volume} />}
              </Row>
              <Row>
                <Label width={104}>Est. Revenue $</Label>
                {isEditable('estRev') ? <TextInput value={data.estRev} onChange={ch('estRev')} ariaLabel="Est. Revenue $" /> : <FieldValue value={data.estRev} />}
              </Row>
              <Row>
                <Label width={104}>Est. Margin %</Label>
                {isEditable('estMargin') ? <TextInput value={data.estMargin} onChange={ch('estMargin')} width={72} ariaLabel="Est. Margin %" /> : <FieldValue value={data.estMargin} width={72} />}
              </Row>
            </MetaCol>
            {/* Revision Statistics — actualized volume / revenue / margin (what
                really happened) sits next to Revenue (what the rep estimated) so
                the two can be compared at a glance. */}
            <MetaCol title="Revision Statistics">
              <Row><Label width={64}>Volume</Label><FieldValue value={data.intelligence?.revision ? fmtIntelVol(data.intelligence.revision.volume) : '—'} /></Row>
              <Row><Label width={64}>Revenue</Label><FieldValue value={data.intelligence?.revision ? fmtIntelRev(data.intelligence.revision.revenue) : '—'} /></Row>
              <Row><Label width={64}>Margin %</Label><FieldValue value={data.intelligence?.revision ? fmtIntelMargin(data.intelligence.revision.margin) : '—'} /></Row>
            </MetaCol>
          </div>
        </div>
        )}
      </section>

      <AlertModal
        open={dateWarning != null}
        title="Check this date"
        message={dateWarning ?? ''}
        closeLabel="Acknowledge"
        onClose={() => setDateWarning(null)}
      />

      {showRevisionHistory && hasQuote && (
        <RevisionHistoryModal
          open={showHistory}
          quoteNumber={data.quoteNumber!}
          currentRevision={data.revisionNumber}
          onClose={() => setShowHistory(false)}
          onSelectRevision={(rev) => {
            setShowHistory(false);
            navigate(`/order-management/quotes/${data.quoteNumber}-${rev}`);
          }}
        />
      )}
    </div>
  );
};

const MetaCol: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  // min-w-0 so a wide row can't blow the grid track out past its share of the
  // container (grid items default to min-width:auto, which is what let the
  // fixed-width Status / Date values overlap the next column).
  <div className="flex flex-col gap-1 min-w-0">
    <div className="text-[0.625rem] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider pb-1 border-b border-dashed border-slate-200 dark:border-slate-700 mb-1">
      {title}
    </div>
    {children}
  </div>
);

export default QuoteDetailHeader;
